/**
 * The tape's move plumbing: one live region, and one dispatch that the pointer, the keyboard and
 * the ⋯ menu all go through.
 *
 * The sequence twin of `useWeekMove`, and just as thin. It decides nothing: `runMoveCommand` gates
 * and applies, `model/tapeMove.ts` words the result, and this puts the two together and holds the
 * sentence for the region to read. What it buys is that the three entry points cannot drift, because
 * there is only one of them.
 *
 * ── Only ONE of the two move hooks may be mounted at a time ──────────────────────────────────
 * Both bind a Pragmatic monitor accepting `kind: 'session'`, and every matching monitor sees every
 * drop — so mounting both would dispatch one gesture twice, as a weekday move and as a positional
 * one. `WeekCanvas` and `TapeCanvas` are what keep that impossible: the screen mounts exactly one.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

import { runMoveCommand } from '~/entities/plan-draft';
import { useAnnouncer } from '~/shared/lib/useAnnouncer';

import { positionMoveAnnouncement } from '../model/tapeMove';

/** What the sentence needs and the command does not: which training moved, and from where. */
export interface TapeMoveContext {
  readonly sessionName: string;
  /** The training's own 0-based position before the move. */
  readonly from: number;
}

export interface TapeMoveState {
  /** Rendered into the tape's always-mounted live region. */
  readonly announcement: string;
  /** Say something without moving anything — the keyboard path's per-arrow preview, and a cancel. */
  readonly announce: (message: string) => void;
  /**
   * Which POSITION a keyboard lift is currently pointed at, or `null` when nothing is lifted.
   *
   * It lives on the tape rather than inside the lifted tile because the tile that has to light up is
   * a SIBLING of the one carrying the lift. Exactly one lift exists at a time, so one value covers
   * it — and it is what stops a sighted keyboard user getting an announcement and nothing on screen.
   */
  readonly pendingTarget: number | null;
  readonly setPendingTarget: (index: number | null) => void;
  /** Send a training to a position, and say what happened. The ONE write behind all three paths. */
  readonly moveDay: (daySlotToken: string, targetIndex: number, context: TapeMoveContext) => void;
  /**
   * The training a completed move owes focus to, or `null`. The week's twin, for the same defect:
   * a commit re-renders the card into another column, the lifted instance unmounts, and focus falls
   * to `<body>`. See `useWeekMove.focusToken` for the argument in full.
   */
  readonly focusToken: string | null;
  /** Clear the signal once the card has taken it, so a later re-mount cannot steal focus. */
  readonly focusTaken: () => void;
}

export function useTapeMove(total: number): TapeMoveState {
  const { t } = useTranslation();
  const { message, announce } = useAnnouncer();
  const [pendingTarget, setPendingTarget] = useState<number | null>(null);
  const [focusToken, setFocusToken] = useState<string | null>(null);

  const moveDay = (daySlotToken: string, targetIndex: number, context: TapeMoveContext): void => {
    setPendingTarget(null);
    setFocusToken(daySlotToken);
    const outcome = runMoveCommand({ kind: 'moveDayToPosition', daySlotToken, targetIndex });
    announce(
      positionMoveAnnouncement(t, outcome, {
        sessionName: context.sessionName,
        from: context.from,
        total,
      }),
    );
  };

  // The latest `moveDay`, so the monitor below can be bound ONCE. Rebinding it every render would
  // tear the monitor down mid-gesture, and a monitor unbound between `onDragStart` and `onDrop`
  // never sees the drop at all. Written in an EFFECT rather than during render — `react-hooks/refs`
  // — which is safe because the only reader is a drop callback, long after effects have run.
  const latest = useRef(moveDay);
  useEffect(() => {
    latest.current = moveDay;
  });

  /**
   * The POINTER path's dispatch, in one place for the whole tape.
   *
   * A monitor rather than one handler per tile, and the reason is the release that lands on NOTHING:
   * view ADR §11 says "a drop outside any target cancels", and only a monitor sees that case. Both
   * endings are therefore decided together, and a cancelled pointer drag takes exactly the path a
   * cancelled keyboard lift does.
   *
   * Everything the sentence needs travels on the drag PAYLOAD, because by the time the drop lands
   * the tree has moved on and reading the name back would report whatever now stands there.
   */
  useEffect(
    () =>
      monitorForElements({
        canMonitor: ({ source }) => source.data.kind === 'session',
        onDrop: ({ source, location }) => {
          setPendingTarget(null);
          const target = location.current.dropTargets[0]?.data;
          // The `kind` is checked before the coordinate is read. Nothing else on this canvas
          // registers a drop target today, but the row is full of elements now and an `index` read
          // off whatever happened to be innermost is a move dispatched to a number that means
          // something else. One assertion, at the only place the coordinate crosses over.
          if (target?.kind !== 'tapePosition') return;
          const index = target.index;
          const daySlotToken = source.data.daySlotToken;
          if (typeof index !== 'number' || typeof daySlotToken !== 'string') return;
          latest.current(daySlotToken, index, {
            sessionName: typeof source.data.sessionName === 'string' ? source.data.sessionName : '',
            from: typeof source.data.from === 'number' ? source.data.from : 0,
          });
        },
      }),
    [],
  );

  return {
    announcement: message,
    announce,
    pendingTarget,
    setPendingTarget,
    moveDay,
    focusToken,
    focusTaken: () => {
      setFocusToken(null);
    },
  };
}
